// ─── Client → Server Messages ───────────────────────────────────────────────

export interface SubscribeMessage {
  type: "subscribe";
  channel: string;
}

export interface UnsubscribeMessage {
  type: "unsubscribe";
  channel: string;
}

export interface PingMessage {
  type: "ping";
}

export type ClientMessage = SubscribeMessage | UnsubscribeMessage | PingMessage;

// ─── Server → Client Messages ───────────────────────────────────────────────

export interface ConnectedMessage {
  type: "connected";
  connectionId: string;
}

export interface SubscribedMessage {
  type: "subscribed";
  channel: string;
}

export interface UnsubscribedMessage {
  type: "unsubscribed";
  channel: string;
}

export interface PongMessage {
  type: "pong";
}

export interface ErrorMessage {
  type: "error";
  message: string;
  code?: string;
}

// ─── Domain Events ──────────────────────────────────────────────────────────

// Stage 2: Voting Events
export interface VotingSessionCreatedEvent {
  type: "voting:session_created";
  channel: string;
  senderId: string;
  payload: { sessionId: string };
}

export interface VotingSessionDeletedEvent {
  type: "voting:session_deleted";
  channel: string;
  senderId: string;
  payload: Record<string, never>;
}

export interface VotingPhaseChangedEvent {
  type: "voting:phase_changed";
  channel: string;
  senderId: string;
  payload: { sessionId: string; phase: string; winnerBookId: string | null };
}

export interface VotingNominationAddedEvent {
  type: "voting:nomination_added";
  channel: string;
  senderId: string;
  payload: {
    nominationId: string;
    bookId: string;
    bookTitle: string;
    userId: string;
  };
}

export interface VotingNominationRemovedEvent {
  type: "voting:nomination_removed";
  channel: string;
  senderId: string;
  payload: { userId: string };
}

export interface VotingRankingSubmittedEvent {
  type: "voting:ranking_submitted";
  channel: string;
  senderId: string;
  payload: { userId: string; rankingCount: number };
}

export type VotingEvent =
  | VotingSessionCreatedEvent
  | VotingSessionDeletedEvent
  | VotingPhaseChangedEvent
  | VotingNominationAddedEvent
  | VotingNominationRemovedEvent
  | VotingRankingSubmittedEvent;

// Stage 3: Reflection Events
export interface ReflectionCreatedEvent {
  type: "reflection:created";
  channel: string;
  senderId: string;
  payload: {
    reflectionId: string;
    segmentNumber: number;
    userId: string;
    type: string;
    title: string | null;
    preview: string;
  };
}

export interface ReflectionReplyCreatedEvent {
  type: "reflection:reply_created";
  channel: string;
  senderId: string;
  payload: {
    replyId: string;
    parentReflectionId: string;
    segmentNumber: number;
    userId: string;
    preview: string;
  };
}

export interface ReflectionReactionToggledEvent {
  type: "reflection:reaction_toggled";
  channel: string;
  senderId: string;
  payload: {
    reflectionId: string;
    emoji: string;
    added: boolean;
    userId: string;
  };
}

export type ReflectionEvent =
  | ReflectionCreatedEvent
  | ReflectionReplyCreatedEvent
  | ReflectionReactionToggledEvent;

// Stage 4: Segment Events
export interface SegmentUnlockedEvent {
  type: "segment:unlocked";
  channel: string;
  senderId: string;
  payload: {
    segmentNumber: number;
    segmentTitle: string;
    currentSegment: number;
  };
}

export interface SegmentLockedEvent {
  type: "segment:locked";
  channel: string;
  senderId: string;
  payload: { segmentNumber: number; currentSegment: number };
}

export type SegmentEvent = SegmentUnlockedEvent | SegmentLockedEvent;

// Stage 5: Gathering Events
export interface GatheringRsvpUpdatedEvent {
  type: "gathering:rsvp_updated";
  channel: string;
  senderId: string;
  payload: {
    gatheringId: string;
    userId: string;
    status: "GOING" | "MAYBE" | "CANT";
  };
}

export interface GatheringUpdatedEvent {
  type: "gathering:updated";
  channel: string;
  senderId: string;
  payload: { gatheringId: string };
}

export interface GatheringCancelledEvent {
  type: "gathering:cancelled";
  channel: string;
  senderId: string;
  payload: { gatheringId: string };
}

export type GatheringEvent =
  | GatheringRsvpUpdatedEvent
  | GatheringUpdatedEvent
  | GatheringCancelledEvent;

// Stage 6: Nudge Events (organizer cockpit)
export interface NudgeReceivedEvent {
  type: "nudge:received";
  channel: string;
  senderId: string;
  payload: {
    nudgeId: string;
    roomId: string;
    receiverId: string;
    hasMessage: boolean;
  };
}

export type NudgeEvent = NudgeReceivedEvent;

// ─── Union Types ────────────────────────────────────────────────────────────

export type DomainEvent =
  | VotingEvent
  | ReflectionEvent
  | SegmentEvent
  | GatheringEvent
  | NudgeEvent;

export type ServerMessage =
  | ConnectedMessage
  | SubscribedMessage
  | UnsubscribedMessage
  | PongMessage
  | ErrorMessage
  | DomainEvent;

// ─── Channel Helpers ────────────────────────────────────────────────────────

export type ChannelType = "club" | "room";

const CHANNEL_REGEX = /^(club|room):([a-zA-Z0-9_-]+)$/;

export function parseChannel(
  channel: string,
): { type: ChannelType; id: string } | null {
  const match = channel.match(CHANNEL_REGEX);
  if (!match) return null;
  return { type: match[1] as ChannelType, id: match[2] };
}

export function clubChannel(clubId: string): string {
  return `club:${clubId}`;
}

export function roomChannel(roomId: string): string {
  return `room:${roomId}`;
}
